import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach access token as Bearer header (cookie-blocked browsers fallback)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("te_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Single-flight refresh: on 401, exchange the refresh token once and retry
let refreshing = null;
async function refreshOnce() {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const rt = localStorage.getItem("te_refresh");
    if (!rt) throw new Error("No refresh token");
    const resp = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: rt }, { withCredentials: true });
    if (resp.data.access_token) localStorage.setItem("te_token", resp.data.access_token);
    if (resp.data.refresh_token) localStorage.setItem("te_refresh", resp.data.refresh_token);
    return resp.data.access_token;
  })().finally(() => { refreshing = null; });
  return refreshing;
}

api.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";
    // Don't try to refresh on auth endpoints or if already retried
    if (status === 401 && !original._retry && !url.includes("/auth/")) {
      original._retry = true;
      try {
        const newAccess = await refreshOnce();
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newAccess}`;
        return api(original);
      } catch (_e) {
        localStorage.removeItem("te_token");
        localStorage.removeItem("te_refresh");
        throw err;
      }
    }
    throw err;
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
